<template>
    <div>
        <div v-if="currentAsset">
            <asset-navbar :filterMarket="currentAsset.market">
                <template v-slot:title>
                    {{ symbol }}
                </template>
                <template v-slot:titleInfo>
                    <span class="ml-2 text-warning"
                        ><strong>${{ formatPrice(currentAsset.price) }}</strong>
                    </span>
                    <span
                        v-if="lowestPosition.open_price"
                        :class="{
                            'text-danger ml-2':
                                getDistance(
                                    lowestPosition.open_price,
                                    currentAsset.price
                                ).value < 0,
                            'ml-2':
                                getDistance(
                                    lowestPosition.open_price,
                                    currentAsset.price
                                ).value >= 0,
                        }"
                        >{{
                            getDistance(
                                lowestPosition.open_price,
                                currentAsset.price
                            ).formatted
                        }}</span
                    >
                </template>
                <template v-slot:subTitle v-if="targetBuyPrice">
                    <span>${{ formatPrice(lowestPosition.open_price) }} </span> 
                    <span class="ml-2">
                        {{ triggerPercent }}%
                    </span>
                    <span class="ml-2 text-info">
                        ${{ buyAmount }} @ ${{ formatPrice(targetBuyPrice) }} 
                    </span>
                </template>
            </asset-navbar>

            <b-alert :show="currentAsset.status === 'disabled'" variant="secondary" class="mb-3">
                Disabled{{ disabledSummary }}. {{ currentAsset.cached_stats?.frozen_at ? 'Stats are frozen at sell-off.' : 'Stats are not frozen and may still update.' }}
            </b-alert>

            <!-- Strategy One Liner -->
            <rounded-container
                variant="highlight"
                v-if="
                    currentAsset.meta &&
                    currentAsset.meta.oneLiner
                "
            >
                <template v-slot:title>Strategy One Liner</template>

                <b-container class="py-3">
                    {{ currentAsset.meta.oneLiner }}
                </b-container>
            </rounded-container>

            <!-- Highlights -->
            <div class="p-2">
                <b-row class="asset-highlights mt-3 text-center" no-gutters>
                    <b-col>
                        <h3>${{ formatHoldings(totalHoldingsValue) }}</h3>
                        <span class="text-secondary"
                            >holdings<br />
                            value</span
                        >
                    </b-col>

                    <!-- <b-col>
                        <h3 v-if="valueLocked < totalHoldingsValue">${{ formatHoldings(valueLocked) }}</h3>
                        <h3 v-else class="text-danger">${{ formatHoldings(valueLocked) }}</h3>
                        <span class="text-secondary">value<br />locked</span>
                    </b-col> -->

                    <b-col v-if="valueLocked < 0">
                        <h3>${{ formatHoldings(totalHoldingsValue) }}</h3>
                        <span class="text-secondary">all<br />profit</span>
                    </b-col>
                    <b-col v-else-if="netProfit >= 0">
                        <h3>${{ formatHoldings(netProfit) }}</h3>
                        <span class="text-secondary"
                            >{{ profitPercentage }}%<br />
                            profit</span
                        >
                    </b-col>
                    <b-col v-else class="text-danger">
                        <h3>-${{ formatHoldings(Math.abs(netProfit)) }}</h3>
                        <span class="text-secondary"
                            >{{ profitPercentage }}%<br />
                            profit</span
                        >
                    </b-col>

                    <!-- <b-col v-if="!gotAssetData" class="text-center text-warning">
                        <h3>${{ formatHoldings(offset * currentAsset.price) }}</h3>
                        <strong class="format-price">{{ formatHoldings(offset) }}</strong><br />
                        <span class="text-secondary">offset</span>
                    </b-col>
                    <b-col v-else-if="offset > 0" class="text-center text-success">
                        <h3>${{ formatHoldings(offset * currentAsset.price) }}</h3>
                        <strong class="format-price">{{ formatHoldings(offset) }}</strong><br />
                        <span class="text-secondary">offset</span>
                    </b-col>
                    <b-col v-else-if="netProfit > 0" class="text-center">
                            <h3>-${{ formatHoldings(Math.abs(offset * currentAsset.price)) }}</h3>
                            <strong class="format-price">{{ formatHoldings(offset) }}</strong><br />
                            <span class="text-secondary">offset</span>
                    </b-col>
                    <b-col v-else class="text-center text-danger">
                            <h3>-${{ formatHoldings(Math.abs(offset * currentAsset.price)) }}</h3>
                            <strong class="format-price">{{ formatHoldings(offset) }}</strong><br />
                            <span class="text-secondary">offset</span>
                    </b-col>  -->

                    <b-col>
                        <h3 v-if="valueLocked > 0">
                            ${{ formatPrice(breakEvenPrice) }}
                        </h3>
                        <h3 v-else>any</h3>
                        <span class="text-secondary">breakeven<br />price</span>
                    </b-col>
                </b-row>

                <b-row
                    class="asset-highlights mt-4 text-center text-secondary"
                    no-gutters
                >
                    <b-col>
                        <h3>${{ formatHoldings(valueLocked) }}</h3>
                        <span class="text-secondary">value<br />locked</span>
                    </b-col>

                    <b-col>
                        <h3>
                            ${{
                                formatHoldings(
                                    currentAsset.cached_stats
                                        .sum_bonus_exit_value || 0
                                )
                            }}
                        </h3>
                        <span class="text-secondary"
                            >all time<br />bonus buys</span
                        >
                    </b-col>

                    <b-col>
                        <h3>
                            ${{
                                formatHoldings(
                                    currentAsset.cached_stats
                                        .stale_positions_value || 0
                                )
                            }}
                        </h3>
                        <span class="text-secondary">stale<br />value</span>
                    </b-col>
                </b-row>
            </div>

            <!-- Recent Events -->
            <rounded-container variant="dark">
                <template v-slot:title>
                    LOWEST BUY ${{ formatPrice(lowestPosition.open_price) || 'N/A' }}

                    <span class="ml-2">&bull;</span>
                    <span class="ml-2">Recent events</span>

                    <span
                        v-if="numberEventsPast60Days > 0"
                        class="text-secondary"
                    >
                        <span class="ml-2">{{ eventsSumValuePast60Days }}</span>
                        <span class="ml-2">{{ numberEventsPast60Days }}</span>
                    </span>

                    <span v-if="numberEventsToday > 0" class="text-secondary">
                        <span class="ml-2">&bull;</span>
                        <span class="ml-2">TODAY</span>
                        <span class="ml-2">{{ eventsSumValue }}</span>
                        <span class="ml-2">{{ numberEventsToday }}</span>
                    </span>
                </template>

                <div
                    class="pt-3 px-1 pb-1"
                    v-if="bubbleChartData.datasets.length"
                >
                    <bubble-chart
                        :chart-data="bubbleChartData"
                        :styles="{
                            position: 'relative',
                            width: '96%',
                            margin: 'auto',
                            height: '300px',
                        }"
                    />
                </div>
                <div class="pt-3 px-1 pb-1" v-else-if="!gotAssetData">
                    <chart-skeleton height="300px" width="96%" />
                </div>

                <b-container>
                    <div v-if="!events.length && !pinnedEvents.length" class="text-center py-3">
                        <span v-if="!gotAssetData">Loading...</span>
                        <span v-else>No recent events</span>
                    </div>

                    <event-list
                        :eventList="events"
                        :pinnedEvents="pinnedEvents"
                        showPins
                        @select-event="showEventInsights"
                    />
                </b-container>
            </rounded-container>

            <!-- Holdings breakdown chart -->
            <!-- Balances -->
            <rounded-container variant="dark">
                <template v-slot:title>Holdings and balances</template>

                <!-- Holdings breakdown chart -->
                <holdings-breakdown-chart
                    v-if="monthlyStats.length"
                    :currentAsset="currentAsset"
                    :monthlyStats="monthlyStats"
                />
                <div v-else-if="!gotAssetData" class="p-2">
                    <chart-skeleton height="40vh" />
                </div>

                <!-- Balances -->
                <b-container
                    class="px-0"
                    v-if="currentAsset.balances.length || monthlyStats.length"
                >
                    <b-row
                        v-for="(balance, rowIndex) in currentAsset.balances"
                        :key="balance.public_id"
                        :class="balanceRowClass(rowIndex, balance.shares)"
                        class="px-3 py-2"
                    >
                        <b-col>
                            {{ balance.bucket.name }}&nbsp;<span
                                v-if="balance.bucket.type === 'trading'"
                                class="text-info"
                                >&#8226;</span
                            >
                        </b-col>
                        <b-col class="format-price">{{
                            formatHoldings(balance.shares)
                        }}</b-col>
                        <b-col class="format-price"
                            >${{
                                formatHoldings(
                                    balance.shares * currentAsset.price
                                )
                            }}</b-col
                        >
                        <b-col>{{
                            formatTimeFromISO(balance.last_check + "Z")
                        }}</b-col>
                    </b-row>

                    <b-row class="px-3 py-2 text-secondary">
                        <b-col> Holdings </b-col>
                        <b-col class="format-price">
                            {{ formatHoldings(totalHoldings) }}
                        </b-col>
                        <b-col>&nbsp;</b-col>
                        <b-col>&nbsp;</b-col>
                    </b-row>
                </b-container>

                <!-- Require asset refresh -->
                <b-container class="py-3 text-center">
                    <div v-if="requireAssetRefresh">
                        <b-overlay
                            :show="assetRefreshing"
                            rounded
                            opacity="0.1"
                            spinner-small
                            spinner-variant="light"
                            class="d-inline-block"
                        >
                            <b-button
                                @click.prevent="refreshAsset"
                                variant="warning"
                                pill
                                >refresh {{ currentAsset.symbol }}</b-button
                            >
                        </b-overlay>
                    </div>
                    <div v-else>
                        <!-- stays live on a disabled asset: the deliberate way to zero leftover bucket shares after a sell-off -->
                        <b-button
                            @click.prevent="addBalance"
                            pill
                            variant="dark"
                            class="mr-2"
                            >add balance</b-button
                        >
                        <b-button v-if="!isDisabled" @click.prevent="suggestBuy" pill
                            >add position</b-button
                        >
                    </div>
                </b-container>
            </rounded-container>

            <rounded-container variant="dark">
                <template v-slot:title> Exits distribution </template>

                <div class="py-1">
                    <b-row
                        v-for="(distribution, rowIndex) in positionDistribution"
                        :key="distribution.bracket"
                        :class="rowClass(rowIndex)"
                        class="px-3 py-2"
                    >
                        <b-col cols="3">{{
                            distribution.bracket.replace("P", "%")
                        }}</b-col>
                        <b-col cols="4" class="format-price"
                            >${{ formatPrice(distribution.value) }}</b-col
                        >
                        <b-col cols="2">{{
                            formatNumber(distribution.starting_price)
                        }}</b-col>
                        <b-col cols="1">⇢</b-col>
                        <b-col cols="2">{{
                            formatNumber(distribution.end_price)
                        }}</b-col>
                    </b-row>
                </div>

                <!-- Exits at target -->
                <asset-exits-at-target v-if="!isDisabled" :currentAsset="currentAsset" />
            </rounded-container>

            <asset-price-sources :symbol="currentAsset.symbol" :lowestOpenPrice="lowestPosition.open_price" />

            <!-- This month's activity -->

            <!-- Edit Asset Meta -->
            <edit-asset-meta
                v-if="!isDisabled"
                :currentAsset="currentAsset"
                @request-asset-refresh="requireRefresh"
            />

            <div class="asset_stats my-5" v-if="!gotAssetData">
                <div class="text-center py-5">
                    <b-spinner
                        variant="primary"
                        type="grow"
                        label="Spinning"
                    ></b-spinner>
                </div>
            </div>
            <div v-else>
                <!-- Open Positions -->
                <rounded-container variant="dark">
                    <template v-slot:title
                        >{{ openPositionsCount }} open positions<span
                            class="text-secondary"
                            v-if="poopPecentage"
                            >, {{ poopPecentage }}% 💩</span
                        ></template
                    >

                    <b-table
                        striped
                        :items="openPositionsFirstHalf"
                        :fields="openPositionFields"
                        class="table table-dark mb-0"
                        :sort-by.sync="openSortBy"
                        :sort-desc.sync="openSortDesc"
                    >
                        <template v-slot:cell(size)="row">
                            {{ row.item.size }}
                            <span
                                v-if="
                                    row.item.open_type ===
                                    'reinvested_from_stale'
                                "
                                >💩</span
                            >
                        </template>
                    </b-table>

                    <div
                        class="text-center p-1"
                        v-if="openPositionsSecondHalf.length"
                    >
                        - - -
                    </div>

                    <b-table
                        striped
                        :items="openPositionsSecondHalf"
                        :fields="openPositionFields"
                        class="table table-dark mb-0"
                        :sort-by.sync="openSortBy"
                        :sort-desc.sync="openSortDesc"
                        thead-tr-class="d-none"
                    >
                        <template v-slot:cell(size)="row">
                            {{ row.item.size }}
                            <span
                                v-if="
                                    row.item.open_type ===
                                    'reinvested_from_stale'
                                "
                                >💩</span
                            >
                        </template>
                    </b-table>
                </rounded-container>

                <!-- Closed Positions -->
                <!-- <rounded-container>
                    <template v-slot:title
                        >{{ closedPositions.length }} closed in the last 30
                        days</template
                    >

                    <b-table
                        striped
                        :items="closedPositions"
                        :fields="closedPositionFields"
                        class="table table-dark mb-0 strikethrough"
                        :sort-by.sync="closedSortBy"
                        :sort-desc.sync="closedSortDesc"
                    >
                    </b-table>
                </rounded-container> -->

                <!-- Groups (locked) -->
                <group-list
                    :groupList="computedGroups"
                    :asset="currentAsset"
                    :requireRefresh.sync="requireAssetRefresh"
                    @asset-add-group="addAssetGroup"
                    @manage-group="manageGroup"
                    @request-refresh="refreshAsset"
                    @require-refresh="requireRefresh"
                />

                <!-- Stats for nerds -->
                <div class="p-2">
                    <h3>Stats for nerds</h3>

                    <b-row
                        class="asset-highlights mt-4 text-center text-secondary"
                        no-gutters
                    >
                        <b-col>
                            <h3>
                                ${{
                                    formatPrice(
                                        currentAsset.cached_stats
                                            .most_exited_at_price
                                    )
                                        ? formatPrice(
                                              currentAsset.cached_stats
                                                  .most_exited_at_price
                                          )
                                        : "N/A"
                                }}
                            </h3>
                            <span class="text-secondary"
                                >price when<br />exited 70%</span
                            >
                        </b-col>
                        <b-col>
                            <h3>
                                ${{
                                    formatPrice(
                                        currentAsset.cached_stats
                                            .last_exit_price
                                    )
                                        ? formatPrice(
                                              currentAsset.cached_stats
                                                  .last_exit_price
                                          )
                                        : "N/A"
                                }}
                            </h3>

                            <span class="text-secondary"
                                >last sim.<br />exit price</span
                            >
                        </b-col>
                    </b-row>
                </div>

                <div class="text-center my-4">
                    <b-button variant="outline-secondary" :to="{ name: 'assetConfig', params: { symbol: currentAsset.symbol } }">
                        Asset config
                    </b-button>
                </div>
            </div>
        </div>
        <div
            v-else-if="assetLoadFailed"
            class="text-center text-secondary py-5"
        >
            Unable to retrieve asset data
        </div>
    </div>
</template>

<script>
// Two separate axes. Do not merge them.
//
// conviction (meta.conviction) — how hard I buy:
//   '100k'     high conviction, pinned to the Dashboard and sorted above 'high' on the Assets page
//   'high'     high conviction
//   null       regular
//   'low'      low conviction
//   'snoozed'  never prompt a buy; sells continue and manual buys stay available
//
// status — do I still own and track this asset:
//   'active'   yes (null on legacy rows means the same)
//   'disabled' sold out; positions written off, the record kept for history
//
// Disabled wins over everything: a disabled asset leaves every live query,
// whatever its conviction. A disable demotes conviction to 'snoozed'; an enable
// leaves it snoozed — the owner raises it deliberately.

import apiMixin from "@/mixins/api";
import formatter from "@/mixins/formatter";
import assetMixin from "@/mixins/assetMixin";
import eventsMixin from "@/mixins/eventsMixin";

import RoundedContainer from "@/components/style/RoundedContainer.vue";
import EditAssetMeta from "@/components/asset/EditAssetMeta.vue";
import AssetExitsAtTarget from "@/components/asset/partials/AssetExitsAtTarget.vue";
import { UNDERWATER_BUY_RATIO } from "@/constants/trading";
import {
    CHART_COLOR_BUY,
    CHART_COLOR_LARGE_BUY,
    CHART_COLOR_PRICE_LINE,
    CHART_COLOR_SELL,
    SHEET_STYLE,
} from "@/constants/ui";
import EventInsights from "@/components/overlays/EventInsights.vue";
import BubbleChart from "@/components/charts/BubbleChart.vue";
import ChartSkeleton from "@/components/charts/ChartSkeleton.vue";
import GroupList from "@/components/GroupList.vue";
import AssetNavbar from "@/components/AssetNavbar.vue";
import EventList from "@/components/EventList.vue";
import HoldingsBreakdownChart from "@/components/asset/partials/HoldingsBreakdownChart.vue";
// Lists every price the last sync heard for this asset; reads the store, fetches nothing.
import AssetPriceSources from "@/components/asset/partials/AssetPriceSources.vue";

export default {
    props: ["symbol", "pid", "assetContext"],
    name: "Asset",
    mixins: [formatter, apiMixin, assetMixin, eventsMixin],
    components: {
        RoundedContainer,
        EditAssetMeta,
        HoldingsBreakdownChart,
        GroupList,
        AssetNavbar,
        AssetExitsAtTarget,
        EventList,
        BubbleChart,
        ChartSkeleton,
        AssetPriceSources,
    },

    data() {
        return {
            currentAsset: null,
            requireAssetRefresh: false,
            assetRefreshing: false,

            gotAssetData: false,
            assetLoadFailed: false,

            monthlyStats: [],

            bubbleChartData: {
                datasets: [],
            },

            openPositions: [],
            openPositionsCount: 0,
            positionDistribution: [],
            closedPositions: [],

            groups: [],
            events: [],
            pinnedEvents: [], // pins at any age, including ones the event window drops
            eventAggregates: null, // Server-computed event aggregates
            chartDays: 0,

            openPositionFields: [
                {
                    key: "open_date",
                    label: "date",
                    sortable: true,
                },
                {
                    key: "size",
                    label: "size",
                    sortable: false,
                },
                {
                    key: "open_price",
                    label: "open",
                    sortable: true,
                },
                {
                    key: "close_price",
                    label: "close",
                    sortable: false,
                },
            ],
            closedPositionFields: [
                {
                    key: "close_date",
                    label: "date",
                    sortable: true,
                },
                {
                    key: "size",
                    label: "size",
                    sortable: false,
                },
                {
                    key: "open_price",
                    label: "open",
                    sortable: true,
                },
                {
                    key: "close_price",
                    label: "close",
                    sortable: false,
                },
            ],
            openSortBy: "open_price",
            openSortDesc: false,
            closedSortBy: "close_date",
            closedSortDesc: true,
        };
    },
    created() {
        if (this.assetContext) this.currentAsset = this.assetContext;
        else
            this.currentAsset = this.$store.getters.getAssetBySymbol(
                this.symbol
            );

        this.retrieveAssetData();
    },
    methods: {
        balanceRowClass(rowIndex, balance) {
            if (balance) return this.rowClass(rowIndex);
            return [this.rowClass, "text-secondary"];
        },
        suggestBuy() {
            this.$emit("suggest-buy", this.currentAsset.symbol);
            this.requireAssetRefresh = true;
        },
        addBalance() {
            this.$emit("asset-add-balance", this.currentAsset);
            this.requireAssetRefresh = true;
        },
        manageGroup(groupData) {
            this.manageGroupModal(groupData);
            this.requireAssetRefresh = true;
        },
        showEventInsights(event) {
            this.$FModal.show(
                {
                    component: EventInsights,
                    ...SHEET_STYLE,
                },
                {
                    anchorEvent: event,
                    eventList: this.events,
                    currentAsset: this.currentAsset,
                    chartDays: this.chartDays,
                    onPinned: (pinned) => { event.pinned = pinned; },
                }
            );
        },
        addAssetGroup() {
            this.$emit("asset-add-group", {
                defaultGroupType: "long_buy",
                asset: this.currentAsset,
            });
            this.requireAssetRefresh = true;
        },
        refreshAsset() {
            this.assetRefreshing = true;
            this.retrieveAssetData().then(() => {
                this.assetRefreshing = false;
                this.requireAssetRefresh = false;
            });
        },
        requireRefresh() {
            this.requireAssetRefresh = true;
        },
        async retrieveAssetData() {
            // can't turn into computed, it must return false by default
            this.gotAssetData = false;

            this.assetLoadFailed = false;

            const response = await this.apiGet(
                `/api/asset/${this.symbol}`,
                {},
                { requestId: "retrieveAssetData" }
            );
            if (!response) {
                this.assetLoadFailed = true;
                return;
            }

            this.currentAsset = response.data.asset;
            this.positionDistribution = response.data.position_distribution;
            this.groups = response.data.groups;
            this.$store.dispatch("updateAssets", [this.currentAsset]);

            this.events = response.data.events;
            this.pinnedEvents = response.data.pinned_events;

            this.monthlyStats = response.data.monthly_stats;

            const {
                data_buys,
                data_largebuys,
                data_exits,
                no_days,
                aggregates,
            } = response.data.chart_n_days_activity;
            this.eventAggregates = aggregates;
            this.chartDays = no_days;

            let lineData = [];
            for (let i = -1 * no_days; i <= 0; i++) {
                lineData.push({
                    y: this.currentAsset.price,
                    x: i,
                    r: 0,
                });
            }
            let bubbleDatasets = [
                {
                    label: "Current price",
                    type: "line",
                    backgroundColor: CHART_COLOR_PRICE_LINE,
                    data: lineData,
                    pointStyle: "dash",
                },
            ];

            if (data_buys.length) {
                bubbleDatasets.push({
                    label: "Buys",
                    type: "bubble",
                    backgroundColor: CHART_COLOR_BUY,
                    data: data_buys,
                });
            }

            if (data_largebuys.length) {
                bubbleDatasets.push({
                    label: "Large Buys",
                    type: "bubble",
                    backgroundColor: CHART_COLOR_LARGE_BUY,
                    data: data_largebuys,
                    pointStyle: "rectRot",
                });
            }

            if (data_exits.length) {
                bubbleDatasets.push({
                    label: "Exits",
                    type: "bubble",
                    backgroundColor: CHART_COLOR_SELL,
                    data: data_exits,
                });
            }

            if (bubbleDatasets.length > 1) {
                this.bubbleChartData = {
                    datasets: bubbleDatasets,
                };
            }

            this.openPositions = response.data.open_positions;
            this.openPositionsCount = response.data.open_positions_count;
            this.closedPositions = response.data.closed_positions;

            this.groups = response.data.groups;

            this.gotAssetData = true;

            return response.data.asset;
        },
    },
    computed: {
        countReinvestedFromStale() {
            let count = 0;

            for (let i = 0; i < this.openPositions.length; i++) {
                if (this.openPositions[i].open_type === "reinvested_from_stale")
                    count += 1;
            }

            return count;
        },
        poopPecentage() {
            if (!this.openPositions.length) return 0;
            return (
                (this.countReinvestedFromStale / this.openPositions.length) *
                100
            ).toFixed();
        },
        tradingHoldings() {
            return this.sumHoldings(this.currentAsset.balances, "trading");
        },
        longHoldings() {
            return this.sumHoldings(this.currentAsset.balances, "long");
        },
        totalHoldings() {
            return (
                parseFloat(this.tradingHoldings) + parseFloat(this.longHoldings)
            );
        },
        totalHoldingsValue() {
            return (this.totalHoldings * this.currentAsset.price).toFixed();
        },
        breakEvenPrice() {
            if (this.totalHoldings === 0) return 0;
            return this.valueLocked / this.totalHoldings;
        },
        sumPendingGroupLockedValue() {
            let sumValue = 0;

            for (let i = 0; i < this.groups.length; i++) {
                if (
                    this.groups[i].stage !== "converted" &&
                    this.groups[i].stage !== "closed"
                ) {
                    sumValue += this.groups[i].value;
                }
            }

            return sumValue;
        },
        valueLocked() {
            return (
                this.sumPendingGroupLockedValue +
                this.currentAsset.cached_stats.open_positions_value -
                (this.currentAsset.cached_stats.sum_bonus_exit_value || 0)
            );
        },
        netProfit() {
            return (
                this.totalHoldingsValue -
                (this.currentAsset.cached_stats.open_positions_value -
                    (this.currentAsset.cached_stats.sum_bonus_exit_value ||
                        0)) -
                this.sumPendingGroupLockedValue
            );
        },
        profitPercentage() {
            return ((this.netProfit / this.valueLocked) * 100).toFixed(1);
        },
        computedGroups() {
            return this.groups;
        },
        openPositionsFirstHalf() {
            return this.openPositionsCount > 30
                ? this.openPositions.slice(0, 20)
                : this.openPositions;
        },
        openPositionsSecondHalf() {
            return this.openPositionsCount > 30
                ? this.openPositions.slice(-10)
                : [];
        },
        // Use client-side mixin for "today" events, server-computed for 60-day range
        eventTotals() {
            return this.aggregateEventData(this.events);
        },
        eventsSumValue() {
            return (
                this.eventTotals[this.currentAsset.market].sumExits -
                this.eventTotals[this.currentAsset.market].sumBuys
            );
        },
        numberEventsToday() {
            return this.eventTotals.numberEventsToday;
        },
        eventsSumValuePast60Days() {
            if (!this.eventAggregates) return 0;
            const market = this.currentAsset.market;
            return (
                this.eventAggregates[market].sum_exits -
                this.eventAggregates[market].sum_buys
            );
        },
        numberEventsPast60Days() {
            return this.eventAggregates?.count_n_days || 0;
        },
        lowestPosition() {
            return this.currentAsset.cached_stats?.lowest_open_position || {};
        },
        targetBuyRatio() {
            // mirrors the underwater buy triggers in Positions.vue
            if (this.currentAsset.conviction === 'snoozed') return null;

            const conviction = this.currentAsset.acts_like_conviction;
            if (conviction === 'high') return UNDERWATER_BUY_RATIO.high;
            if (conviction === 'low') return UNDERWATER_BUY_RATIO.low;
            if (conviction === null) return UNDERWATER_BUY_RATIO.null;
            return null;
        },
        targetBuyPrice() {
            if (!this.lowestPosition.open_price || !this.targetBuyRatio) return null;
            return this.lowestPosition.open_price * this.targetBuyRatio;
        },
        triggerPercent() {
            return Math.round((this.targetBuyRatio - 1) * 100);
        },
        buyAmount() {
            const resolve = this.$store.getters.resolveBuySize;
            let positions = resolve(this.currentAsset).positions;

            // a steep drop below the target price can match a larger buy rule than the target does
            if (this.targetBuyPrice) {
                const atTarget = resolve({
                    ...this.currentAsset,
                    price: this.targetBuyPrice,
                }).positions;
                positions = Math.max(positions, atTarget);
            }

            return positions * this.$store.state.positions.defaultPositionSize;
        },
        isDisabled() {
            return this.currentAsset.status === 'disabled';
        },
        disabledSummary() {
            const disables = this.currentAsset.meta?.disables;
            if (!disables) return '';

            // newest unenabled entry, matching EnableAsset.disableRecord so the banner and the modal agree
            let latest = null;
            for (let i = disables.length - 1; i >= 0; i--) {
                if (!disables[i].enabled_at) {
                    latest = disables[i];
                    break;
                }
            }
            if (!latest || !latest.disabled_at) return '';

            return ` on ${latest.disabled_at.split('T')[0]} at $${latest.exit_price}`;
        },
    },
    watch: {
        symbol() {
            // when going through assets without changing the page, a force reload is required
            this.retrieveAssetData();
        },
    },
};
</script>

<style scoped>
.asset-highlights h3 {
    margin-bottom: 0px;
}
.asset-highlights .text-secondary {
    font-size: 16px;
}
</style>